import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoBanner from "@/components/site/PromoBanner";
import SEO from "@/components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

const useBootstrap = (fn) => {
  useEffect(() => {
    fn();
  }, [fn]);
};

const NewsletterIssue = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/newsletter/${slug}`);
      setIssue(data);
    } catch (err) {
      if (err?.response?.status === 404) navigate("/newsletter", { replace: true });
      setIssue(null);
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useBootstrap(load);

  return (
    <main data-testid="newsletter-issue-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO
        title={issue ? issue.title : "Newsletter"}
        description={issue?.excerpt || "Vaymilo newsletter issue."}
        path={`/newsletter/${slug}`}
      />
      <PromoBanner />
      <Header />

      <section className="flex-1 bg-white border-b border-[#E7E7E2]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <Link
            to="/newsletter"
            data-testid="newsletter-back"
            className="inline-flex items-center gap-2 font-manrope text-sm text-[#111111]/65 hover:text-[#111111] transition-colors"
          >
            <ArrowLeft size={14} /> All issues
          </Link>

          {loading && (
            <div data-testid="issue-loading" className="mt-10 space-y-4">
              <div className="h-12 bg-[#F7F7F4] border border-[#E7E7E2] rounded-lg animate-pulse w-3/4" />
              <div className="h-6 bg-[#F7F7F4] border border-[#E7E7E2] rounded-lg animate-pulse w-1/2" />
              <div className="mt-10 h-4 bg-[#F7F7F4] rounded animate-pulse" />
              <div className="h-4 bg-[#F7F7F4] rounded animate-pulse w-11/12" />
              <div className="h-4 bg-[#F7F7F4] rounded animate-pulse w-10/12" />
            </div>
          )}

          {!loading && issue && (
            <article className="mt-10">
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2563EB]">
                Issue · {fmtDate(issue.published_at || issue.created_at)}
              </div>
              <h1
                data-testid="issue-title"
                className="mt-4 font-sora text-[40px] sm:text-[52px] font-extrabold tracking-tight leading-[1.05] text-[#111111]"
              >
                {issue.title}
              </h1>
              <p className="mt-5 font-manrope text-[17px] md:text-[18px] text-[#111111]/75 leading-relaxed max-w-2xl">
                {issue.excerpt}
              </p>

              <div data-testid="issue-author" className="mt-8 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#111111] flex items-center justify-center shrink-0">
                  <span className="font-sora text-[16px] font-extrabold text-white leading-none">
                    V<span className="text-[#2563EB]">.</span>
                  </span>
                </div>
                <div>
                  <div className="font-manrope text-[14px] font-semibold text-[#111111]">
                    The Vaymilo Studio
                  </div>
                  <div className="font-manrope text-[12.5px] text-[#111111]/55">
                    hello@vaymilo.com · {fmtDate(issue.published_at || issue.created_at)}
                  </div>
                </div>
              </div>

              <div className="mt-10 h-px bg-[#E7E7E2]" />

              <div
                data-testid="issue-body"
                className="mt-10 font-manrope text-[16px] md:text-[17px] text-[#111111]/85 leading-[1.75] whitespace-pre-line max-w-2xl"
              >
                {issue.body}
              </div>

              <div className="mt-16 border-t border-[#E7E7E2] pt-10">
                <p className="font-manrope text-[15px] text-[#111111]/65">
                  Liked this? Take the 30-second quiz to get the kit that fits your work.
                </p>
                <Link
                  to="/start"
                  className="mt-4 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-sm px-5 py-3 rounded-lg transition-colors"
                >
                  Find my kit
                </Link>
              </div>
            </article>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default NewsletterIssue;
